/**
 * AktionVideoHero
 * ---------------
 * Full-width cinematic Video-Hero am Kopf der /aktion-Landing.
 *
 * Zustandsmodell (3 Phasen):
 *   1. `muted-loop`         — Initial: Video autoplay stumm im Loop.
 *   2. `playing-with-sound` — Nach erster User-Gesture ODER Klick auf
 *                             Ton-Button/Video. Ton an, `loop=false`,
 *                             Video läuft genau EINMAL durch.
 *   3. `ended-idle`         — Nach Ende der Ton-Runde ODER wenn das
 *                             Video zu >50 % aus dem Viewport scrollt.
 *                             Ton aus, `loop=true` (Video läuft optisch
 *                             stumm weiter), Play-Overlay sichtbar.
 *
 * Übergänge:
 *   • `muted-loop`         → `playing-with-sound`  (Auto-Unmute-Gesture ODER Ton-Button ODER Video-Klick)
 *   • `playing-with-sound` → `ended-idle`          (`ended` / ≥99 % Playback / IntersectionObserver Out)
 *   • `ended-idle`         → `playing-with-sound`  (Klick auf Play-Overlay / Video / Ton-Button)
 *
 * Analytics (alle via `sendReliable` / Beacon-API):
 *   • `video_hero_sound_on`       — manueller Ton-Button-Klick, erster Unmute
 *   • `video_hero_auto_sound`     — Auto-Unmute via Gesture (trigger=scroll/click/touch/keyboard)
 *   • `video_hero_completed`      — erste Ton-Runde vollständig durchgelaufen
 *   • `video_hero_scrolled_out`   — IntersectionObserver hat Video aus Viewport gemeldet (nur während `playing-with-sound`)
 *   • `video_hero_replay`         — Klick auf Play-Overlay nach idle-Phase
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import type { VideoHeroConfig } from "@/lib/useAktionsSeite";
import { trackInteraction } from "@/lib/analytics";

type Props = {
    cfg: VideoHeroConfig;
};

type Phase = "muted-loop" | "playing-with-sound" | "ended-idle";

export default function AktionVideoHero({ cfg }: Props) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const containerRef = useRef<HTMLElement | null>(null);
    const [phase, setPhase] = useState<Phase>("muted-loop");
    const [progress, setProgress] = useState<number>(0);
    /** Steuert das weiche Ausblenden des „STUMM"-Indikators nach dem
     *  ersten Unmute (statt hartem Verschwinden). */
    const [mutedLabelVisible, setMutedLabelVisible] = useState<boolean>(true);
    /** Guards, die pro Zyklus des Ton-Playbacks zurückgesetzt werden. */
    const completedCycleRef = useRef<boolean>(false);
    /** Wird permanent auf `true` gesetzt, sobald der Nutzer irgendwann
     *  interagiert hat — Auto-Gesture-Listener werden dann nicht mehr
     *  benötigt und der Stumm-Indikator nicht wieder eingeblendet. */
    const hasEverInteractedRef = useRef<boolean>(false);
    /** Ref auf den aktuellen Phase-Wert, damit Listener-Handler ohne
     *  Re-Setup den frischen Wert lesen können. */
    const phaseRef = useRef<Phase>(phase);
    phaseRef.current = phase;

    // Fallback-Kaskade: Desktop-URL leer → nur Mobile. Mobile leer + Desktop
    // gesetzt → Desktop-Fallback für Mobile.
    const desktopSrc = cfg.video_desktop_url?.trim() || "";
    const mobileSrc = cfg.video_mobile_url?.trim() || desktopSrc;
    const finalDesktopSrc = desktopSrc || mobileSrc;
    const muted = phase !== "playing-with-sound";

    /** Wechselt in „playing-with-sound": Ton an, Loop aus, Video ab 0.
     *  Wird von Ton-Button, Video-Klick, Play-Overlay und Auto-Gesture
     *  aufgerufen. Kein-op wenn wir bereits in dieser Phase sind. */
    const startWithSound = (
        source: "button" | "scroll" | "click" | "touch" | "keyboard" | "video" | "replay",
    ) => {
        const v = videoRef.current;
        if (!v) return;
        if (phaseRef.current === "playing-with-sound") return;
        const wasIdle = phaseRef.current === "ended-idle";
        completedCycleRef.current = false;
        hasEverInteractedRef.current = true;
        v.loop = false;
        v.muted = false;
        setPhase("playing-with-sound");
        // Weicher Fade-out beim ersten Unmute (danach ist Label eh weg).
        window.setTimeout(() => setMutedLabelVisible(false), 1000);
        // Aus `muted-loop` und `ended-idle` heraus starten wir immer von 0
        // — der Ton soll konsistent von Anfang beginnen. Nur wenn das
        // Video im Initial-Loop schon > 5 s gelaufen ist UND wir gerade
        // aus dem Auto-Gesture-Trigger kommen, springen wir nicht zurück
        // (spart einen visuellen Bruch bei sehr langem Passivkonsum).
        const skipRewind = source !== "button"
            && source !== "video"
            && source !== "replay"
            && v.currentTime >= 5;
        if (!skipRewind) {
            try { v.currentTime = 0; } catch { /* SecurityError egal */ }
        }
        v.play().catch(() => {
            /* Autoplay-Restriktionen in seltenen Fällen — im echten Browser
             * kein Problem, weil source stets echte User-Gesture ist. */
        });
        try {
            if (source === "button") {
                trackInteraction("video_hero_sound_on", { src: v.currentSrc || finalDesktopSrc });
            } else if (source === "video" || source === "replay" || wasIdle) {
                trackInteraction("video_hero_replay", {
                    trigger: source, src: v.currentSrc || finalDesktopSrc,
                });
            } else {
                trackInteraction("video_hero_auto_sound", {
                    trigger: source, src: v.currentSrc || finalDesktopSrc,
                });
            }
        } catch { /* nicht kritisch */ }
    };

    /** Beendet die Ton-Runde: Ton aus, Loop zurück an, weiter im Hinter-
     *  grund stumm laufend, Play-Overlay wird sichtbar. */
    const endToIdle = (reason: "ended" | "scrolled-out") => {
        const v = videoRef.current;
        if (!v) return;
        if (phaseRef.current !== "playing-with-sound") return;
        setPhase("ended-idle");
        v.muted = true;
        v.loop = cfg.loop;
        if (reason === "scrolled-out") {
            // Aus Viewport gescrollt → wir pausieren (kein Sinn CPU zu
            // verbrennen wenn niemand hinsieht).
            try { v.pause(); } catch { /* egal */ }
            try {
                trackInteraction("video_hero_scrolled_out", {
                    src: v.currentSrc || finalDesktopSrc,
                });
            } catch { /* nicht kritisch */ }
        } else {
            // Regulär durchgelaufen — Video springt zurück und läuft im
            // Loop stumm weiter, damit die Fläche optisch belebt bleibt.
            try { v.currentTime = 0; } catch { /* egal */ }
            v.play().catch(() => { /* ok */ });
        }
    };

    // Ton-Button-Handler: schaltet zwischen den beiden „Sound"-Phasen.
    const handleSoundClick = () => {
        if (phaseRef.current === "playing-with-sound") {
            // Explizit stumm — Nutzer will nicht zurück in Loop, sondern
            // ins Overlay-Idle (analog Video-Ende).
            endToIdle("scrolled-out");
        } else {
            startWithSound("button");
        }
    };

    // Progress-Ticker + Ende-Erkennung
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onTime = () => {
            if (!v.duration || !isFinite(v.duration)) return;
            setProgress((v.currentTime / v.duration) * 100);
        };
        const onEnded = () => {
            // `completed` einmalig loggen; Übergang in Idle nur aus
            // „playing-with-sound".
            if (!completedCycleRef.current && phaseRef.current === "playing-with-sound") {
                completedCycleRef.current = true;
                try {
                    trackInteraction("video_hero_completed", {
                        duration_s: Math.round(v.duration || 0),
                    });
                } catch { /* nicht kritisch */ }
                endToIdle("ended");
            }
        };
        const onProgress = () => {
            if (completedCycleRef.current) return;
            if (phaseRef.current !== "playing-with-sound") return;
            if (!v.duration || !isFinite(v.duration)) return;
            if (v.currentTime / v.duration >= 0.99) onEnded();
        };
        v.addEventListener("timeupdate", onTime);
        v.addEventListener("timeupdate", onProgress);
        v.addEventListener("ended", onEnded);
        return () => {
            v.removeEventListener("timeupdate", onTime);
            v.removeEventListener("timeupdate", onProgress);
            v.removeEventListener("ended", onEnded);
        };
    }, []);

    /** Auto-Unmute bei erster User-Gesture. Listener werden nur einmalig
     *  registriert und beim ersten Trigger entfernt. */
    useEffect(() => {
        if (hasEverInteractedRef.current) return;
        const onGesture = (ev: Event) => {
            if (hasEverInteractedRef.current) return;
            // Klick auf Ton-Button / Video / Play-Overlay laufen bereits
            // über ihre eigenen Handler.
            const target = ev.target;
            if (
                target instanceof Element &&
                target.closest(
                    '[data-testid="aktion-video-hero-sound-btn"], ' +
                    '[data-testid="aktion-video-hero-video"], ' +
                    '[data-testid="aktion-video-hero-replay"]',
                )
            ) {
                return;
            }
            let trigger: "scroll" | "click" | "touch" | "keyboard" = "click";
            if (ev.type === "scroll") trigger = "scroll";
            else if (ev.type === "touchstart") trigger = "touch";
            else if (ev.type === "keydown") trigger = "keyboard";
            startWithSound(trigger);
            cleanup();
        };
        const opts: AddEventListenerOptions = { passive: true, capture: true };
        const cleanup = () => {
            window.removeEventListener("scroll", onGesture, opts);
            window.removeEventListener("click", onGesture, opts);
            window.removeEventListener("touchstart", onGesture, opts);
            window.removeEventListener("keydown", onGesture, opts);
        };
        window.addEventListener("scroll", onGesture, opts);
        window.addEventListener("click", onGesture, opts);
        window.addEventListener("touchstart", onGesture, opts);
        window.addEventListener("keydown", onGesture, opts);
        return cleanup;
    }, []);

    /** IntersectionObserver — sobald das Video zu > 50 % aus dem Viewport
     *  ist, während wir gerade mit Ton spielen: pausieren + mute + Idle.
     *  Kommt es zurück ins Bild, spielen wir es nur stumm weiter (Nutzer
     *  muss klicken für Ton). */
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    const outOfView = e.intersectionRatio < 0.5;
                    if (outOfView) {
                        if (phaseRef.current === "playing-with-sound") {
                            endToIdle("scrolled-out");
                        }
                    } else {
                        // Zurück im Bild → stummes Loop-Playback fortsetzen,
                        // falls wir gerade in idle sind (Overlay bleibt).
                        const v = videoRef.current;
                        if (v && phaseRef.current === "ended-idle" && v.paused) {
                            v.play().catch(() => { /* ok */ });
                        }
                    }
                }
            },
            { threshold: [0, 0.5, 1] },
        );
        io.observe(c);
        return () => io.disconnect();
    }, []);

    // Kein Rendern, wenn beide URLs leer sind.
    if (!desktopSrc && !mobileSrc) return null;

    return (
        <section
            ref={containerRef}
            data-testid="aktion-video-hero"
            className="relative w-full pt-4 pb-1 px-3 md:px-6 lg:px-8"
            style={{ background: "transparent" }}
        >
            <div
                className="relative w-full mx-auto rounded-3xl overflow-hidden bg-black"
                style={{
                    aspectRatio: "9 / 16",
                    maxHeight: "60vh",
                    boxShadow: "0 20px 60px -20px rgba(15, 24, 48, 0.35), 0 0 0 1px rgba(212, 168, 85, 0.15)",
                }}
            >
                <style>{`
                    @media (min-width: 768px) {
                        [data-testid="aktion-video-hero"] > div {
                            aspect-ratio: 21 / 9 !important;
                            max-height: 70vh !important;
                        }
                    }
                `}</style>

                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full cursor-pointer"
                    style={{ objectFit: "cover" }}
                    autoPlay
                    muted
                    loop={cfg.loop}
                    playsInline
                    preload="metadata"
                    poster={cfg.poster_url || undefined}
                    onClick={() => startWithSound("video")}
                    data-testid="aktion-video-hero-video"
                >
                    {desktopSrc && (
                        <source
                            src={finalDesktopSrc}
                            media="(min-width: 768px)"
                            type="video/mp4"
                        />
                    )}
                    <source src={mobileSrc} type="video/mp4" />
                </video>

                {/* Gradient-Overlay unten (immer sichtbar für Übergang) */}
                <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3"
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)",
                    }}
                />

                {/* Stumm-Indikator — nur solange nie interagiert wurde */}
                {cfg.muted_label && mutedLabelVisible && !hasEverInteractedRef.current && (
                    <div
                        className="absolute top-3 left-3 md:top-4 md:left-4 pointer-events-none"
                        style={{
                            opacity: muted ? 1 : 0,
                            transition: "opacity 1000ms ease-out",
                        }}
                        data-testid="aktion-video-hero-muted-label"
                    >
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[9px] md:text-[10px] font-medium tracking-wider"
                            style={{
                                background: "rgba(0,0,0,0.55)",
                                color: "#F5E6C8",
                                border: "1px solid rgba(212,168,85,0.35)",
                                backdropFilter: "blur(4px)",
                            }}
                        >
                            <VolumeX size={10} />
                            {cfg.muted_label}
                        </span>
                    </div>
                )}

                {/* Nach dem ersten Ton-Durchlauf (`ended-idle`) sowie nach
                    Viewport-Out: Video läuft optisch stumm weiter im Loop,
                    KEIN Dimm-/Blur-Overlay — nur der kleine Ton-Button
                    (unten) erscheint erneut mit gewechseltem Text
                    „🔊 ERNEUT MIT TON". */}

                {/* Zentraler Ton-Button — in allen Phasen. Text passt sich
                    dem Kontext an:
                      • muted-loop         → cfg.sound_button_text (default „🔊 MIT TON ANSEHEN")
                      • ended-idle         → „🔊 ERNEUT MIT TON"
                      • playing-with-sound → „🔇 STUMMSCHALTEN" */}
                {cfg.show_sound_button && (
                    <button
                        type="button"
                        onClick={handleSoundClick}
                        data-testid="aktion-video-hero-sound-btn"
                        aria-label={
                            muted
                                ? (phase === "ended-idle" ? "Erneut mit Ton abspielen" : cfg.sound_button_text)
                                : "Stummschalten"
                        }
                        className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-40 inline-flex items-center gap-1.5 h-fit w-fit px-3 md:px-4 py-1.5 md:py-2 rounded-full font-semibold uppercase tracking-widest text-[10px] md:text-xs transition"
                        style={{
                            background: "rgba(15, 12, 8, 0.72)",
                            border: "1px solid #D4A855",
                            color: "#F5E6C8",
                            backdropFilter: "blur(8px)",
                            boxShadow:
                                "0 8px 32px -8px rgba(0,0,0,0.55), 0 0 40px -12px rgba(212,168,85,0.5)",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(30, 24, 16, 0.85)";
                            e.currentTarget.style.color = "#FFFDF9";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(15, 12, 8, 0.72)";
                            e.currentTarget.style.color = "#F5E6C8";
                        }}
                    >
                        {muted ? (
                            <>
                                <Volume2 size={12} />
                                <span>
                                    {phase === "ended-idle"
                                        ? "🔊 ERNEUT MIT TON"
                                        : (cfg.sound_button_text || "🔊 MIT TON ANSEHEN")}
                                </span>
                            </>
                        ) : (
                            <>
                                <VolumeX size={12} />
                                <span>🔇 STUMMSCHALTEN</span>
                            </>
                        )}
                    </button>
                )}

                {/* Progress-Bar */}
                <div
                    className="absolute inset-x-0 bottom-0 pointer-events-none"
                    style={{ height: 2, background: "rgba(255,255,255,0.12)" }}
                    data-testid="aktion-video-hero-progress"
                >
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            background: "#A78150",
                            transition: "width 120ms linear",
                        }}
                    />
                </div>
            </div>
        </section>
    );
}
